import { useEffect, useState, useRef } from 'react';
import { resolveOptions } from '../utils/options';


/**
* Generic options loader used by both ModalContainer & FilterContainer.
* Accepts a list of descriptors that at minimum contain { key, options?, loadOptions? }.
* `augment` lets the caller add/transform (e.g., prepend an "All" option for filters).
*/
export default function useOptionsMap(descriptors = [], { augment } = {}) {
    const [map, setMap] = useState({});
    const cacheRef = useRef({}); // { [key]: { value, promise } }


    // Build a stable signature that ignores function identities
    const sig = JSON.stringify((descriptors || []).map(d => ({
        key: d.key,
        hasStaticOptions: Array.isArray(d.options) && d.options.length > 0,
        hasLoader: typeof d.loadOptions === 'function',
        allValue: d.allValue,
    })));


    useEffect(() => {
        let cancelled = false;
        (async () => {
            const entries = await Promise.all(
                (descriptors || []).map(async (d) => {
                    // Prefer static options when present
                    if (Array.isArray(d.options) && d.options.length) {
                        const base = d.options;
                        const final = typeof augment === 'function' ? augment(d, base) : base;
                        return [d.key, final];
                    }


                    // Cached value
                    const cache = cacheRef.current[d.key];
                    if (cache && cache.value) {
                        const final = typeof augment === 'function' ? augment(d, cache.value) : cache.value;
                        return [d.key, final];
                    }


                    // In-flight fetch
                    if (cache && cache.promise) {
                        const data = await cache.promise;
                        const final = typeof augment === 'function' ? augment(d, data) : data;
                        return [d.key, final];
                    }


                    // Kick off loader and cache both promise and resolved value
                    const promise = (async () => {
                        const base = await resolveOptions(d);
                        cacheRef.current[d.key] = { ...(cacheRef.current[d.key] || {}), value: base };
                        return base;
                    })();
                    cacheRef.current[d.key] = { ...(cacheRef.current[d.key] || {}), promise };
                    const data = await promise;
                    const final = typeof augment === 'function' ? augment(d, data) : data;
                    return [d.key, final];
                })
            );
            if (!cancelled) setMap(Object.fromEntries(entries));
        })();
        return () => { cancelled = true; };
        // Depend on the stable signature only (keys/structure), not function identities
    }, [sig, augment, descriptors]);


    return map;
}